<template>
  <div class="page roles-page">
    <div class="page-header">
      <h1 class="page-title">Perfis</h1>
      <p class="page-subtitle">Crie e edite perfis com permissões. Atribua perfis aos usuários na Equipe.</p>
    </div>

    <div class="card-list">
      <article
        v-for="role in sortedRoles"
        :key="role.id"
        class="card card--with-icon"
      >
        <div class="card-icon-wrap roles-card-icon">
          <i class="material-icons">admin_panel_settings</i>
        </div>
        <div class="card-content">
          <h3 class="card-title card-primary">{{ role.name || role.id }}</h3>
          <p class="card-meta card-secondary">
            {{ (role.permissions || []).length }} permissão(ões)
          </p>
        </div>
        <div class="card-actions">
          <button
            type="button"
            class="btn-icon"
            aria-label="Editar"
            @click="openEdit(role)"
          >
            <i class="material-icons">edit</i>
          </button>
          <button
            type="button"
            class="btn-icon btn-icon-danger"
            aria-label="Excluir"
            @click="handleDeleteClick(role)"
          >
            <i class="material-icons">delete_outline</i>
          </button>
        </div>
      </article>
    </div>

    <button
      type="button"
      class="btn-fab"
      @click="openAdd"
      aria-label="Novo perfil"
    >
      <i class="material-icons">add</i>
    </button>

    <Drawer :model-value="showModal" @update:model-value="showModal = $event">
      <div class="modal-header">
        <h2 class="modal-title">{{ editingId ? 'Alterar perfil' : 'Novo perfil' }}</h2>
        <button
          type="button"
          class="modal-close"
          aria-label="Fechar"
          @click="showModal = false"
        >
          <i class="material-icons">close</i>
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="save">
          <div class="form-group">
            <label for="role-name">Nome</label>
            <input
              id="role-name"
              type="text"
              v-model="roleName"
              placeholder="Ex: Admin, Atendente"
              :aria-invalid="!!nameError"
            />
            <p v-if="nameError" class="field-error" role="alert">{{ nameError }}</p>
          </div>
          <div class="form-group">
            <label class="roles-permissions-label">Permissões</label>
            <p class="field-hint">Marque as permissões que este perfil concede.</p>
            <div class="roles-permissions-list">
              <label
                v-for="perm in ALL_PERMISSIONS"
                :key="perm"
                class="roles-permission-item"
              >
                <input
                  type="checkbox"
                  :value="perm"
                  :checked="selectedPermissions.includes(perm)"
                  @change="togglePermission(perm)"
                  class="roles-permission-checkbox"
                />
                <span class="roles-permission-name">{{ PERMISSION_LABELS[perm] || perm }}</span>
              </label>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" :disabled="saveLoading" @click="showModal = false">
          Cancelar
        </button>
        <button type="button" class="btn btn-primary" :disabled="saveLoading" @click="save">
          {{ saveLoading ? 'Salvando...' : 'Salvar' }}
        </button>
      </div>
    </Drawer>

    <ConfirmDialog
      v-model="showDeleteConfirm"
      title="Excluir perfil"
      message="Tem certeza que deseja excluir este perfil? Usuários com este perfil perderão as permissões associadas."
      confirm-label="Excluir"
      :confirm-loading="deleteLoading"
      @confirm="doDeleteRole"
    />
  </div>
</template>

<script>
import Drawer from '@/components/Drawer.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import { collection, doc, setDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { firestore } from '@/firebase';
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useToast } from '@/composables/useToast';
import { useUserStore } from '@/stores/user';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '@/firebase';
import { ALL_PERMISSIONS, PERMISSION_LABELS } from '@/constants/permissions';

export default {
  name: 'Roles',
  components: { Drawer, ConfirmDialog },
  setup() {
    const { show: showToast } = useToast();
    const userStore = useUserStore();
    const roles = ref({});
    const showModal = ref(false);
    const showDeleteConfirm = ref(false);
    const deleteTargetId = ref(null);
    const saveLoading = ref(false);
    const deleteLoading = ref(false);
    const editingId = ref(null);
    const roleName = ref('');
    const selectedPermissions = ref([]);
    const nameError = ref('');
    let unsubRoles = null;

    const canRunMigration = computed(() => userStore.hasPermission('roles:manage'));
    const seedLoading = ref(false);
    const migrateLoading = ref(false);
    const migrationMessage = ref('');
    const migrationError = ref(false);

    const sortedRoles = computed(() => {
      const list = Object.entries(roles.value).map(([id, data]) => ({ id, ...data }));
      return list.sort((a, b) => (a.name || a.id || '').localeCompare(b.name || b.id || ''));
    });

    function openAdd() {
      editingId.value = null;
      roleName.value = '';
      selectedPermissions.value = [];
      nameError.value = '';
      showModal.value = true;
    }

    async function runSeedRoles() {
      seedLoading.value = true;
      migrationMessage.value = '';
      migrationError.value = false;
      try {
        const functions = getFunctions(app, 'us-central1');
        const seedRoles = httpsCallable(functions, 'seedRoles');
        const res = await seedRoles();
        const data = res.data || {};
        migrationMessage.value = data.created > 0 ? `Perfis padrão criados: ${data.created}.` : 'Nenhum perfil novo criado (já existem).';
      } catch (err) {
        migrationError.value = true;
        migrationMessage.value = err.message || 'Erro ao criar perfis.';
      } finally {
        seedLoading.value = false;
      }
    }

    async function runMigrateUsers() {
      migrateLoading.value = true;
      migrationMessage.value = '';
      migrationError.value = false;
      try {
        const functions = getFunctions(app, 'us-central1');
        const migrateUsersToRoleIds = httpsCallable(functions, 'migrateUsersToRoleIds');
        const res = await migrateUsersToRoleIds();
        const data = res.data || {};
        migrationMessage.value = data.updated > 0 ? `Usuários migrados: ${data.updated}.` : 'Nenhum usuário precisou de migração.';
      } catch (err) {
        migrationError.value = true;
        migrationMessage.value = err.message || 'Erro ao migrar usuários.';
      } finally {
        migrateLoading.value = false;
      }
    }

    function openEdit(role) {
      editingId.value = role.id;
      roleName.value = role.name || '';
      selectedPermissions.value = Array.isArray(role.permissions) ? [...role.permissions] : [];
      nameError.value = '';
      showModal.value = true;
    }

    function togglePermission(perm) {
      const idx = selectedPermissions.value.indexOf(perm);
      if (idx === -1) {
        selectedPermissions.value = [...selectedPermissions.value, perm].sort();
      } else {
        selectedPermissions.value = selectedPermissions.value.filter((p) => p !== perm);
      }
    }

    function handleDeleteClick(role) {
      deleteTargetId.value = role.id;
      showDeleteConfirm.value = true;
    }

    async function doDeleteRole() {
      if (!deleteTargetId.value) return;
      deleteLoading.value = true;
      try {
        await deleteDoc(doc(firestore, 'roles', deleteTargetId.value));
        deleteTargetId.value = null;
        showDeleteConfirm.value = false;
        showToast('Perfil excluído.', 'success');
      } catch (err) {
        showToast('Não foi possível excluir. Tente novamente.', 'error');
      } finally {
        deleteLoading.value = false;
      }
    }

    async function save() {
      nameError.value = '';
      const name = (roleName.value || '').trim();
      if (!name) {
        nameError.value = 'Informe o nome do perfil.';
        return;
      }
      saveLoading.value = true;
      try {
        const docData = {
          name,
          permissions: [...selectedPermissions.value].sort(),
        };
        if (editingId.value) {
          await setDoc(doc(firestore, 'roles', editingId.value), docData, { merge: true });
          showToast('Perfil alterado.', 'success');
        } else {
          const id = name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
          const finalId = id || `role_${Date.now()}`;
          await setDoc(doc(firestore, 'roles', finalId), docData);
          showToast('Perfil criado.', 'success');
        }
        showModal.value = false;
      } catch (err) {
        showToast('Não foi possível salvar. Tente novamente.', 'error');
      } finally {
        saveLoading.value = false;
      }
    }

    onMounted(() => {
      unsubRoles = onSnapshot(collection(firestore, 'roles'), (snapshot) => {
        const next = {};
        snapshot.docs.forEach((d) => {
          next[d.id] = { id: d.id, ...d.data() };
        });
        roles.value = next;
      }, (err) => {
        console.error('Roles snapshot error', err);
      });
    });

    onUnmounted(() => {
      if (typeof unsubRoles === 'function') unsubRoles();
    });

    return {
      ALL_PERMISSIONS,
      PERMISSION_LABELS,
      roles,
      sortedRoles,
      showModal,
      showDeleteConfirm,
      saveLoading,
      deleteLoading,
      editingId,
      roleName,
      selectedPermissions,
      nameError,
      openAdd,
      openEdit,
      togglePermission,
      handleDeleteClick,
      doDeleteRole,
      save,
      canRunMigration,
      seedLoading,
      migrateLoading,
      migrationMessage,
      migrationError,
      runSeedRoles,
      runMigrateUsers,
    };
  },
};
</script>

<style scoped>
.roles-page {
  padding: var(--space-md) 0;
}

.roles-migration-section {
  margin-bottom: var(--space-lg);
  padding: var(--space-md);
  border: 1px solid var(--color-border, #e0e0e0);
  border-radius: var(--radius-md, 8px);
  background: var(--color-bg-soft, #f8f8f8);
}

.roles-migration-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 var(--space-sm) 0;
}

.roles-migration-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-sm);
  margin-top: var(--space-sm);
}

.roles-migration-message {
  margin-top: var(--space-sm);
  font-size: 0.875rem;
  color: var(--color-text-secondary);
}

.roles-migration-error {
  color: var(--color-error);
}

.roles-card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.roles-card-icon .material-icons {
  font-size: 2rem;
  opacity: 0.8;
}

.roles-permissions-label {
  display: block;
  font-weight: 600;
  margin-bottom: 2px;
}

.roles-permissions-list {
  margin-top: var(--space-md);
  max-height: 280px;
  overflow-y: auto;
}

.roles-permission-item {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  cursor: pointer;
  font-weight: normal;
  padding: var(--space-sm) var(--space-md);
}

.roles-permission-item:hover {
  background: var(--color-bg-soft, #f8f8f8);
}

.roles-permission-checkbox {
  width: 1.125rem;
  height: 1.125rem;
  flex-shrink: 0;
  accent-color: var(--color-accent);
  cursor: pointer;
}

.roles-permission-name {
  font-size: 0.9375rem;
  color: var(--color-text);
}

.field-error {
  font-size: 0.875rem;
  color: var(--color-error);
  margin-top: var(--space-xs);
  margin-bottom: 0;
}

.field-hint {
  font-size: 0.8rem;
  color: var(--color-text-muted);
  margin-top: var(--space-xs);
  margin-bottom: 0;
}
</style>
